import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';
import { UiReplenishModal } from '@app/components/model/ticket/ui-replenish/ui-replenish';

interface InvRow {
    fv: number;         // valor facial (céntimos)
    label: string;      // "5.00", "0.50"…
    stacker: number;    // piezas disponibles (dispensables)
    cassette: number;   // piezas en cassette (Total − Stacker)
    total: number;      // piezas totales en máquina
    amount: number;     // importe de la denominación (euros)
    devid: number;      // 1 = billete, 2 = moneda
}

interface CollectRow {
    fv: number;
    label: string;
    stacker: number;    // máximo que se puede sacar (solo del stacker)
    devid: number;
    request: number;    // piezas que el operario quiere sacar
}

type Klass = 'notes' | 'coins' | 'both';

/**
 * "Backoffice" de Glory (BrueBox): panel de administración del cajón, ya que
 * Glory no tiene app propia como Cashlogy. Protegido por PIN (por defecto 1515).
 *
 * Vistas:
 *  - `menu`    → resumen + inventario (billetes/monedas, Stacker/Cassette/Total/
 *                Importe) + acciones (Cargar cambio, Sacar/Collect, desbloqueos,
 *                Device Reset), según el submodelo que reporta el backend.
 *  - `collect` → "sacar" efectivo del STACKER a la boca de salida: pestañas
 *                Billetes/Monedas/Ambos, elección manual por denominación (del
 *                stacker; del cassette NO se puede sacar salvo abriendo el cajón)
 *                o "Todo", y START.
 *
 * El backend (`glory.rs`) mapea BACKOFFICE (inventario + `variant`), COLLECT
 * (option/partial/class/denominations), UNLOCK_NOTES/UNLOCK_COINS y RESET.
 */
@Component({
    selector: 'ui-modal-glory-inventory',
    templateUrl: './ui-glory-inventory.html',
    styleUrls: [ './ui-glory-inventory.scss' ]
})
export class UiGloryInventoryModal extends languageSupport implements OnInit, OnDestroy {
    public _connection: WSConnection = null;
    public _target: string = 'GLORY';
    private _pin: string = '1515';
    // Submodelo Glory: '' (genérico) / 'CI-5' / 'CI-10' / 'CI-10X' / 'CI-15'.
    private _variant: string = '';

    public status: 'pin' | 'loading' | 'ready' | 'error' = 'pin';
    public view: 'menu' | 'collect' = 'menu';
    public pinInput: string = '';
    public pinError: boolean = false;
    public errorMessage: string = null;

    public notes: InvRow[] = [];
    public coins: InvRow[] = [];
    public grandTotal: number = 0;     // euros
    public totalStacker: number = 0;   // euros
    public totalCassette: number = 0;  // euros

    // Acciones de admin.
    public busy: boolean = false;
    public actionMsg: string = null;
    public actionOk: boolean = false;

    // Subvista Collect ("sacar" a la boca / "guardar en cofre" al cassette).
    public collectClass: Klass = 'both';
    public collectRows: CollectRow[] = [];
    // Destino del Collect: 'exit' = boca de salida (Sacar efectivo) / 'cassette' =
    // cofre interno (Guardar en cofre, para retirar en el cierre).
    public collectDest: 'exit' | 'cassette' = 'exit';

    // Callback que el padre (ui-tab-cash) pasa para REGISTRAR en el arqueo cada
    // retirada (Sacar efectivo -> salida) o carga (Cargar cambio -> entrada). El
    // modal no tiene `place`/`session` para crear el CashChange, así que delega.
    private _recordMovement: ((eur: number, type: 'withdrawal' | 'load') => void) | null = null;
    // Céntimos movidos en el último COLLECT correcto (lo lee `_action`).
    private _lastCollectedCents: number = 0;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'GLORY';
        const _p = this.params.get('pin');
        if (_p != null && String(_p).length > 0){
            this._pin = String(_p);
        }
        this._recordMovement = this.params.get('recordMovement') || null;
        // El submodelo lo reporta el backend en la respuesta de BACKOFFICE.
    }

    // Inventario DISPENSABLE (stacker) por denominación en céntimos { fv: piezas }
    // para que el arqueo Entrada/Salida no vuelva a pedir CONTENTS (#2). Solo el
    // stacker: es lo que la rejilla de arqueo cuenta como dispensable.
    private _inventoryMap(): { [fv: number]: number } {
        let _out: { [fv: number]: number } = {};
        for (let _r of [...(this.notes || []), ...(this.coins || [])]){
            if (_r && _r.fv > 0 && _r.stacker > 0){
                _out[_r.fv] = (_out[_r.fv] || 0) + _r.stacker;
            }
        }
        return _out;
    }

    ngOnInit() {
        this.status = 'pin';
    }

    ngOnDestroy() {
        super.OnDestroy();
    }

    /*****************************/
    /* SUBMODELO / VISIBILIDAD   */
    /*****************************/
    // CI-10/CI-10X → desbloqueo billetes+monedas; CI-15 → monedas; '' → ambos.
    get ShowUnlockNotes(): boolean {
        return this._variant === '' || this._variant === 'CI-10' || this._variant === 'CI-10X';
    }
    get ShowUnlockCoins(): boolean {
        return this._variant === '' || this._variant === 'CI-10' || this._variant === 'CI-10X' || this._variant === 'CI-15';
    }
    // CI-5: solo monedas. No tiene "Sacar efectivo" ni "Guardar en cofre" genéricos;
    // en su lugar, la única acción de retirada es "Retirada cofre monedas" (vacía el
    // cofre/overflow box a la boca, COLLECT_COFFER en el backend).
    get IsCI5(): boolean {
        return this._variant === 'CI-5' || this._variant === 'CI5';
    }
    get ShowCollectCoffer(): boolean {
        return this.IsCI5;
    }

    /*****************************/
    /* PIN                       */
    /*****************************/
    OnPinKey(digit: string) {
        if (this.pinInput.length >= 8){ return; }
        this.pinInput += digit;
        this.pinError = false;
        this.change.markForCheck();
    }
    OnPinBack() {
        this.pinInput = this.pinInput.slice(0, -1);
        this.pinError = false;
        this.change.markForCheck();
    }
    async OnPinSubmit() {
        // La validación la hace el BACKEND (env GLORY_PIN, configurable). No
        // comparamos aquí contra un PIN fijo: mandamos el introducido por WS y,
        // si el backend responde code:'PIN', volvemos a pedirlo. Así el gate
        // vale de verdad y respeta el PIN configurado en el print_service.
        if (!this.pinInput || this.pinInput.length === 0){ return; }
        this._pin = this.pinInput;
        await this._load();
    }

    OnClose() {
        // No cerrar con una operación de dinero en curso (COLLECT/RESET/UNLOCK):
        // cerrar desconectaría la conexión compartida dejando la operación física
        // sin respuesta ni conciliación. Mismo criterio que el aborto del cobro.
        if (this.busy){ return; }
        this.modalCtrl.dismiss();
    }

    // "Entrada/Salida de efectivo" (arqueo): la rejilla vive en ui-tab-cash, así
    // que cerramos el backoffice señalando al control de caja que la abra.
    OnCashInOut() {
        if (this.busy){ return; }
        // Pasamos el inventario ya cargado para que el arqueo NO vuelva a pedir
        // CONTENTS (#2) — SOLO si la carga fue correcta ('ready'). Si el inventario
        // falló ('error'), lo omitimos para que el arqueo re-lea y no muestre
        // ceros/stale (el error-state ofrece Entrada/Salida como fallback).
        let _inv = (this.status === 'ready') ? this._inventoryMap() : null;
        this.modalCtrl.dismiss({ action: 'cashinout', inventory: _inv });
    }

    async OnRefresh() {
        if (this.busy){ return; }
        await this._load();
    }

    /*****************************/
    /* ACCIONES DE MENÚ          */
    /*****************************/
    // Desbloqueo de cierres CI-10/CI-10X/CI-15.
    async OnUnlockNotes() {
        await this._action('UNLOCK_NOTES', '@smartdrawer_unlock_notes_ok', '@smartdrawer_unlock_error');
    }
    async OnUnlockCoins() {
        await this._action('UNLOCK_COINS', '@smartdrawer_unlock_coins_ok', '@smartdrawer_unlock_error');
    }
    // Retirada del COFRE de monedas (exclusivo CI-5): vacía el cofre/overflow box a la
    // boca (COLLECT_COFFER). El cajón elige; registramos la SALIDA si movió efectivo.
    async OnCollectCoffer() {
        const ok = await this._action('COLLECT_COFFER', '@smartdrawer_coffer_ok', '@smartdrawer_coffer_error');
        if (this._lastCollectedCents > 0){
            this._recordMovement?.(this._lastCollectedCents / 100, 'withdrawal');
        }
        if (ok || this._lastCollectedCents > 0){
            await this._load();   // refrescar inventario
        }
    }
    // Reset del dispositivo (ResetOperation) — recuperación / mantenimiento.
    async OnDeviceReset() {
        await this._action('RESET', '@smartdrawer_reset_ok', '@smartdrawer_reset_error');
    }

    // "Cargar cambio" (Replenishment from Entrance): abre el modal Start/Finish
    // reutilizando la conexión. Al cerrar, refresca el inventario.
    async OnReplenish() {
        if (this.busy || !this._connection){ return; }
        let modal = await this.modalCtrl.create({
            component: UiReplenishModal,
            cssClass: 'modal-cashguard',
            backdropDismiss: false,
            componentProps: { connection: this._connection, target: this._target }
        });
        await modal.present();
        let _res = await modal.onDidDismiss();
        // Carga de cambio = ENTRADA de efectivo: registrar el importe cargado
        // (totalLoaded en euros, lo devuelve el modal al finalizar) en el arqueo.
        let _loaded = (_res && _res.data && Number(_res.data.totalLoaded)) || 0;
        if (_loaded > 0){
            this._recordMovement?.(_loaded, 'load');
        }
        await this._load();
    }

    /*****************************/
    /* COLLECT ("SACAR")         */
    /*****************************/
    // "Sacar efectivo": mueve del stacker a la BOCA de salida (para retirar).
    OnOpenCollect() {
        this.actionMsg = null;
        this.collectDest = 'exit';
        this.setCollectClass('both');
        this.view = 'collect';
        this.change.markForCheck();
    }

    // "Guardar en cofre": mueve del stacker al CASSETTE interno (Option 0), para
    // retirar en el cierre. Por defecto billetes (las monedas suelen quedarse para
    // dar cambio); el operario puede cambiar de pestaña.
    OnOpenStore() {
        this.actionMsg = null;
        this.collectDest = 'cassette';
        this.setCollectClass('notes');
        this.view = 'collect';
        this.change.markForCheck();
    }

    // Título/etiquetas de la subvista según el destino.
    get CollectTitle(): string {
        return this.collectDest === 'cassette'
            ? this.tr('@smartdrawer_store_title')
            : this.tr('@smartdrawer_collect_title');
    }
    OnBackToMenu() {
        this.view = 'menu';
        this.actionMsg = null;
        this.change.markForCheck();
    }

    // Construye las filas de Collect con el STACKER (dispensable) de la clase.
    // Del cassette no se puede sacar salvo abriendo el cajón, así que solo se
    // ofrece lo que hay en stacker.
    setCollectClass(k: Klass) {
        this.collectClass = k;
        const src: InvRow[] = k === 'notes' ? this.notes
            : k === 'coins' ? this.coins
            : [...this.notes, ...this.coins];
        this.collectRows = src
            .filter(r => r.stacker > 0)
            .map(r => ({ fv: r.fv, label: r.label, stacker: r.stacker, devid: r.devid, request: 0 }));
        this.change.markForCheck();
    }

    incRequest(row: CollectRow) {
        if (row.request < row.stacker){ row.request++; this.change.markForCheck(); }
    }
    decRequest(row: CollectRow) {
        if (row.request > 0){ row.request--; this.change.markForCheck(); }
    }
    setMaxRequest(row: CollectRow) {
        row.request = row.stacker; this.change.markForCheck();
    }
    clearRequest(row: CollectRow) {
        row.request = 0; this.change.markForCheck();
    }

    // Importe/piezas seleccionados.
    get CollectPieces(): number { return this.collectRows.reduce((a, r) => a + r.request, 0); }
    get CollectAmount(): number { return this.collectRows.reduce((a, r) => a + r.request * r.fv, 0) / 100; }
    get CanCollect(): boolean { return !this.busy && this.CollectPieces > 0; }

    // Destino del Collect (Option del CollectRequest):
    //   - cassette (Guardar en cofre) = 0 (FCC_REQ_COLLECTION_TO_CST).
    //   - boca de salida (Sacar efectivo): monedas=1, billetes=2, ambos=3.
    private _collectOption(): number {
        if (this.collectDest === 'cassette'){ return 0; }
        return this.collectClass === 'coins' ? 1 : this.collectClass === 'notes' ? 2 : 3;
    }

    // Sacar exactamente lo pedido (partial=0, denominaciones explícitas).
    async OnCollectStart() {
        if (!this.CanCollect){ return; }
        const denominations = this.collectRows
            .filter(r => r.request > 0)
            .map(r => ({ denomination: r.fv, numItems: r.request, devid: r.devid }));
        const ok = await this._action(
            { command: 'COLLECT', option: this._collectOption(), partial: 0, denominations },
            '@smartdrawer_collect_ok', '@smartdrawer_collect_error'
        );
        // Registrar SIEMPRE lo que SÍ salió (aunque la op reporte error por una
        // retirada PARCIAL): `_lastCollectedCents` es el importe real movido y el
        // recorder es no-op si es 0 o el destino fue el cassette.
        this._recordCollectMovement();
        // Refrescar el inventario si se movió algo, aunque haya sido PARCIAL
        // (ok=false con dinero fuera): si no, el inventario mostrado quedaría stale.
        if (ok || this._lastCollectedCents > 0){
            await this._load();          // refresca inventario
            this.setCollectClass(this.collectClass);
        }
    }

    // Sacar TODO el stacker de la clase (partial=1, class).
    async OnCollectAll() {
        const cls = this.collectClass === 'both' ? 'all' : this.collectClass;
        const ok = await this._action(
            { command: 'COLLECT', option: this._collectOption(), partial: 1, class: cls },
            '@smartdrawer_collect_ok', '@smartdrawer_collect_error'
        );
        this._recordCollectMovement();   // registrar lo que SÍ salió, aunque sea parcial
        if (ok || this._lastCollectedCents > 0){   // refrescar también tras un parcial
            await this._load();
            this.setCollectClass(this.collectClass);
        }
    }

    // Registra en el arqueo un COLLECT correcto SOLO si fue a la BOCA (Sacar
    // efectivo = retirada/SALIDA). "Guardar en cofre" (destino cassette) es un
    // movimiento INTERNO stacker->cassette: el dinero sigue en el cajón, no se
    // registra (la salida real ocurre al abrir el cajón y retirar el cofre).
    private _recordCollectMovement() {
        if (this.collectDest === 'exit' && this._lastCollectedCents > 0){
            this._recordMovement?.(this._lastCollectedCents / 100, 'withdrawal');
        }
    }

    /*****************************/
    /* INTERNALS                 */
    /*****************************/
    private async _load() {
        this.status = 'loading';
        this.change.markForCheck();
        try {
            if (!this._connection){ this._fail(this.tr('@cashguard_connection_failed')); return; }
            const resp = await this._connection.Send(this._target, { command: 'BACKOFFICE', pin: this._pin });
            const body = this._body(resp);
            // PIN incorrecto: el backend lo rechaza (code:'PIN'). Volvemos a la
            // pantalla del PIN en vez de a la de error genérica.
            if (body && body['code'] === 'PIN'){
                this.status = 'pin';
                this.pinError = true;
                this.pinInput = '';
                this.change.markForCheck();
                return;
            }
            if (!body || body['status'] === 'error' || !Array.isArray(body['denominations'])){
                this._fail(body && body['message']);
                return;
            }
            this._fill(body);
            this.status = 'ready';
        } catch (e) {
            this._fail();
        }
        this.change.markForCheck();
    }

    private _fill(body: any) {
        this._variant = (body['variant'] || '').toString().toUpperCase();
        const rows: InvRow[] = (body['denominations'] || []).map((d: any) => {
            const fv = this._num(d, 'denomination', 0);
            const total = this._num(d, 'total', 0);
            const stacker = this._num(d, 'stacker', 0);
            const cassette = this._num(d, 'cassette', Math.max(0, total - stacker));
            return {
                fv,
                label: (fv / 100).toFixed(2),
                stacker, cassette, total,
                amount: this._num(d, 'amount', fv * total) / 100,
                devid: fv >= 500 ? 1 : 2
            } as InvRow;
        });
        this.notes = rows.filter(r => r.fv >= 500);
        this.coins = rows.filter(r => r.fv < 500);
        this.grandTotal = this._num(body, 'grandTotal', 0) / 100;
        this.totalStacker = this._num(body, 'totalStacker', 0) / 100;
        this.totalCassette = this._num(body, 'totalCassette', 0) / 100;
    }

    // Subtotales de columna (fila Total).
    ColStacker(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.stacker, 0); }
    ColCassette(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.cassette, 0); }
    ColTotal(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.total, 0); }
    ColAmount(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.amount, 0); }
    // Importe (€) por pool: fv (céntimos) × piezas de ese pool. Para la línea de
    // total por columna del pie (stacker/cassette en €, no solo piezas).
    ColStackerAmount(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.fv * r.stacker, 0) / 100; }
    ColCassetteAmount(rows: InvRow[]): number { return rows.reduce((a, r) => a + r.fv * r.cassette, 0) / 100; }

    // Manda un comando de admin. `cmd` = nombre o payload. Devuelve si fue OK.
    private async _action(cmd: string | any, okKey: string, errKey: string): Promise<boolean> {
        if (this.busy || !this._connection){ return false; }
        this.busy = true;
        this.actionMsg = null;
        this.change.markForCheck();
        let ok = false;
        this._lastCollectedCents = 0;   // se rellena si esta op movió efectivo
        try {
            const payload = (typeof cmd === 'string') ? { command: cmd } : cmd;
            // El backend EXIGE el PIN también en las acciones de admin (no solo
            // al entrar): COLLECT/RESET/UNLOCK. Reusamos el PIN ya validado.
            payload['pin'] = this._pin;
            const resp = await this._connection.Send(this._target, payload);
            const body = this._body(resp);
            ok = !!(resp && resp.exec === 'SUCCESS' && (!body || body['status'] !== 'error'));
            this.actionOk = ok;
            // Si la operación devuelve un importe movido (COLLECT -> `totalCollected`,
            // en céntimos), lo mostramos junto al mensaje —como Prosegur (' · 12,34 €')—
            // para que el operario vea CUÁNTO se sacó/vació. Se añade SIEMPRE que el
            // importe sea positivo, aunque la operación reporte error (retirada
            // PARCIAL): el operario debe ver lo que SÍ salió, igual que Prosegur.
            // Otras acciones (UNLOCK/RESET) no lo traen: `-1` -> no se añade importe.
            const _collected = this._num(body, 'totalCollected', -1);
            if (_collected > 0){
                this._lastCollectedCents = _collected;   // para registrar la retirada
            }
            const _amt = _collected > 0
                ? ' · ' + (_collected / 100).toFixed(2) + this.tr('@currency')
                : '';
            // En ERROR: si el backend manda un CÓDIGO conocido como prefijo del mensaje
            // (p.ej. "DEVICE_BUSY: ..."), mostramos el texto TRADUCIDO por idioma; si no,
            // el genérico. NO mostramos el mensaje crudo del cajón (viene en inglés).
            let _errMsg = this.tr(errKey);
            const _bmsg = (body && typeof body['message'] === 'string') ? body['message'] : '';
            if (!ok && _bmsg.indexOf('DEVICE_BUSY') === 0){
                _errMsg = this.tr('@smartdrawer_device_busy');
            }
            this.actionMsg = (ok ? this.tr(okKey) : _errMsg) + _amt;
        } catch (e) {
            this.actionOk = false;
            this.actionMsg = this.tr(errKey);
        }
        this.busy = false;
        this.change.markForCheck();
        return ok;
    }

    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@smartdrawer_backoffice_error');
        this.change.markForCheck();
    }
}
